import React, { useState, useRef } from 'react';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import { useAccountInformation } from '../../contexts/AccountInformationContext';
import TextInput from '../../muiComponents/TextInput/TextInput';
import LoadedOptionsTypeahead from '../../muiComponents/LoadedOptionsTypeahead';
import endpoints from '../../utils/commonVariables/endpoints';
import { validateForm } from '../../utils/validateForms';
import api from '../../services/api';
import convertToOvariant from '../../utils/convertToOvariant';
import { useNotification } from '../../contexts/NotificationsContext';
import accountInformationViewModel from '../../utils/viewModelStructures/accountInformation';
import { Chip } from '@mui/material';
import Grid from '../../muiComponents/Grid';
import GridChild from '../../muiComponents/GridChild';
import {
    gridSpacing20,
    gridSpacing30,
    singleXL_DoubleLG_ColumnGrid,
} from '../../utils/commonVariables/grid';
import {
    readOnlyEntities,
    savableEntities,
} from '../../utils/commonVariables/entityKeys';
import Tooltip from '../../muiComponents/Tooltip';
import Icon from '../../muiComponents/Icon';
import { FeedbackMessages } from '../../utils/commonVariables/messages';
import { ProfileViewMode } from '../../types/profile/profileTypes';
import { useODataMultipleQueries } from '../../customHooks/useODataMultipleQueries';
import {
    adaptAccountInformationDDBBToState,
    adaptAccountInformationStateToDDBB,
} from '../../services/profile/accountInformationAdapters';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseIcon from '@mui/icons-material/Close';
import AccountInfoReadOnlyField from './AccountInfoReadOnlyField';

const EditView = () => {
    const {
        accountInfo,
        setAccountInfo,
        handleViewModeChange,
        setIsLoadingAccountInformation,
    } = useAccountInformation();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const [errors, setErrors] = useState({});
    const [isValidated, setIsValidated] = useState(false);
    const initialState = useRef({ ...accountInfo });
    const handleTextInput = e => {
        const {
            target: { value, name },
        } = e;
        setAccountInfo(prevState => {
            return { ...prevState, [name]: value };
        });
    };

    const handleTypeahead = (e, value) => {
        const {
            target: { name },
        } = e;
        setAccountInfo(prevState => {
            return { ...prevState, [name]: parseInt(value?.Id) || 0 };
        });
    };

    const handleMultipleTypeahead = (e, newValues) => {
        setAccountInfo(prevState => {
            const prev = prevState.OwnerTypes || [];
            const newIds = newValues.map(v => parseInt(v.Id));
            const prevVisibleIds = prev
                .filter(v => !v.Deleted)
                .map(v => parseInt(v.Id));

            // Previously deleted entries are restored rather than added again.
            const added = newValues
                .filter(
                    value =>
                        !prev.some(
                            previous =>
                                parseInt(previous.Id, 10) ===
                                parseInt(value.Id, 10)
                        )
                )
                .map(v => ({ ...v, __isAdded: true }));

            // Detect removed: in previous visible but not in newValues
            const removedIds = prevVisibleIds.filter(
                id => !newIds.includes(id)
            );

            // Keep existing items, marking removed ones
            const updated = prev
                .filter(v => {
                    const id = parseInt(v.Id);
                    if (removedIds.includes(id)) {
                        // If it was just added this session, drop it entirely
                        if (v.__isAdded) return false;
                        // Otherwise keep it (will be marked Deleted)
                        return true;
                    }
                    return true;
                })
                .map(v => {
                    if (v.Deleted && newIds.includes(parseInt(v.Id, 10))) {
                        return { ...v, Deleted: false };
                    }
                    if (removedIds.includes(parseInt(v.Id)) && !v.__isAdded) {
                        return { ...v, Deleted: true };
                    }
                    return v;
                });

            return {
                ...prevState,
                OwnerTypes: [...updated, ...added],
            };
        });
    };
    const { countries, ownerTypes, currencies } = useODataMultipleQueries({
        queries: [
            {
                key: 'countries',
                endpoint: {
                    entityName: readOnlyEntities.Country,
                    filter: 'Active',
                    orderBy: 'Caption asc',
                },
            },
            {
                key: 'ownerTypes',
                endpoint: {
                    entityName: readOnlyEntities.OwnerType,
                    select: 'Id,Name,LocalizedCaption,Caption',
                    filter: 'Enabled',
                    orderBy: 'Name asc',
                },
            },
            {
                key: 'currencies',
                endpoint: {
                    entityName: readOnlyEntities.Currency,
                    select: 'Id,Name',
                    filter: 'Enabled',
                    orderBy: 'Name asc',
                },
            },
        ],
        dependencies: [],
        cached: true,
    });

    const fieldsToValidate = {
        Name: { value: accountInfo.Name, validators: ['requiredString'] },
        Email: {
            value: accountInfo.Email,
            validators: ['requiredString', 'email'],
        },
        ContactPerson: {
            value: accountInfo.ContactPerson,
            validators: ['requiredString'],
        },
        Phone: {
            value: accountInfo.Phone,
            validators: ['requiredString', 'phone'],
        },
        IdOwnerType: {
            value: accountInfo.IdOwnerType,
            validators: ['requiredIdEntity'],
        },
        IdCountry: {
            value: accountInfo.IdCountry,
            validators: ['requiredIdEntity'],
        },
        ...(!initialState.current?.IdCurrency
            ? {
                  IdCurrency: {
                      value: accountInfo.IdCurrency,
                      validators: ['requiredIdEntity'],
                  },
              }
            : {}),
    };

    const handleValidate = () => {
        if (isValidated) {
            const validationErrors = validateForm(fieldsToValidate);
            let newErrors = {};
            for (let newError of validationErrors) {
                newErrors[newError.inputKey] = newError.errorMessage;
            }
            setErrors(newErrors);
        }
    };

    const handleSend = async () => {
        setIsValidated(true);
        const validationErrors = validateForm(fieldsToValidate);
        let newErrors = {};
        for (let newError of validationErrors) {
            newErrors[newError.inputKey] = newError.errorMessage;
        }
        setErrors(newErrors);
        const isValid = validationErrors.length === 0;
        if (isValid) {
            setIsLoadingAccountInformation(true);
            const adaptedDataToDDBB =
                adaptAccountInformationStateToDDBB(accountInfo);
            const objToSend = convertToOvariant(
                savableEntities.HotelOwner,
                adaptedDataToDDBB,
                accountInfo.Id,
                accountInformationViewModel
            );
            try {
                const res = await api.post(endpoints.commonSave, objToSend);
                if (res.status === 201) {
                    handleViewModeChange(ProfileViewMode.READ_ONLY);
                    setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
                    setAccountInfo(
                        adaptAccountInformationDDBBToState(res.data)
                    );
                } else {
                    setErrorMessage(FeedbackMessages.COMMON_ERROR);
                }
                setIsLoadingAccountInformation(false);
            } catch (error) {
                console.log(error);
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
                setIsLoadingAccountInformation(false);
            }
        }
    };

    return (
        <>
            <Grid rowSpacing={gridSpacing30} columnSpacing={gridSpacing20}>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <TextInput
                        labelText={'property_name'}
                        value={accountInfo.Name}
                        errorMessage={errors.Name}
                        id={'Name'}
                        handleChange={handleTextInput}
                        onBlur={handleValidate}
                        required
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <TextInput
                        labelText={'contact_person'}
                        value={accountInfo.ContactPerson}
                        errorMessage={errors.ContactPerson}
                        id={'ContactPerson'}
                        handleChange={handleTextInput}
                        onBlur={handleValidate}
                        required
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <TextInput
                        labelText={'phone'}
                        value={accountInfo.Phone}
                        errorMessage={errors.Phone}
                        id={'Phone'}
                        handleChange={handleTextInput}
                        onBlur={handleValidate}
                        required
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <TextInput
                        labelText={'your_email'}
                        value={accountInfo.Email}
                        errorMessage={errors.Email}
                        id={'Email'}
                        handleChange={handleTextInput}
                        onBlur={handleValidate}
                        required
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'Country'}
                        errorMessage={errors.IdCountry}
                        id={'IdCountry'}
                        options={countries?.data || []}
                        idValue={accountInfo.IdCountry}
                        handleChange={handleTypeahead}
                        onBlur={handleValidate}
                        required
                    />
                </GridChild>

                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    {initialState.current?.IdCurrency ? (
                        <AccountInfoReadOnlyField
                            labelText={'currency'}
                            value={accountInfo?.Currency}
                            tooltipText='currency_readonly'
                        />
                    ) : (
                        <LoadedOptionsTypeahead
                            labelText={'currency'}
                            errorMessage={errors.IdCurrency}
                            id={'IdCurrency'}
                            options={currencies?.data || []}
                            idValue={accountInfo.IdCurrency}
                            handleChange={handleTypeahead}
                            onBlur={handleValidate}
                            required
                        />
                    )}
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <AccountInfoReadOnlyField
                        labelText={'hotel_chain'}
                        value={accountInfo?.HotelChain}
                        tooltipText='hotel_chain_readonly'
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'access_type'}
                        id={'IdOwnerType'}
                        options={ownerTypes?.data || []}
                        idValue={accountInfo.IdOwnerType}
                        handleChange={handleTypeahead}
                        onBlur={handleValidate}
                        errorMessage={errors.IdOwnerType}
                        required
                    />
                </GridChild>
                <GridChild gridProps={singleXL_DoubleLG_ColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'additional_access_types'}
                        options={ownerTypes?.data || []}
                        value={(accountInfo.OwnerTypes || []).filter(
                            v => !v.Deleted
                        )}
                        onChange={handleMultipleTypeahead}
                        optionsLabelKey={'Name'}
                        onBlur={handleValidate}
                        multiple
                        disableCloseOnSelect
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option.Id || index}
                                    label={option.label || option.Name}
                                    size='small'
                                    deleteIcon={<CloseIcon fontSize='small' />}
                                    sx={{
                                        backgroundColor: 'transparent',
                                    }}
                                />
                            ))
                        }
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps } = props;
                            const SelectionIcon = selected
                                ? CheckBoxIcon
                                : CheckBoxOutlineBlankIcon;

                            return (
                                <li key={key} {...optionProps}>
                                    <SelectionIcon
                                        fontSize='small'
                                        style={{
                                            marginRight: 8,
                                            padding: 9,
                                            boxSizing: 'content-box',
                                            color: selected
                                                ? 'var(--primary-400)'
                                                : 'inherit',
                                        }}
                                    />
                                    {option.Name}
                                </li>
                            );
                        }}
                    />
                </GridChild>
            </Grid>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
                <Button
                    variant='outlined'
                    onClick={() => {
                        handleViewModeChange(ProfileViewMode.READ_ONLY);
                        setAccountInfo(initialState.current);
                    }}
                >
                    Cancel
                </Button>
                <Button variant='contained' onClick={handleSend}>
                    Update
                </Button>
            </CardActions>
        </>
    );
};

export default EditView;
